// src/pages/Competitions/components/CompetitionDrawer.jsx
import React, { useEffect } from "react";
import {
  FaCalendarAlt,
  FaClipboardList,
  FaExternalLinkAlt,
  FaMapMarkerAlt,
  FaPercentage,
  FaTimes,
  FaUser,
  FaUserCheck,
  FaUsers,
} from "react-icons/fa";
import { MdOutlineSportsKabaddi } from "react-icons/md";

import {
  formatDateLongFR,
  getCompetitionTemporalStatus,
  getLevelLabel,
  getLevelTone,
  getOrganizerLabel,
  getPresencePercent,
  openCompetitionPublicPage,
  safeDisciplines,
} from "../competitionsListingUtils";

export default function CompetitionDrawer({
  competition,
  organizer,
  stats,
  onClose,
}) {
  useEffect(() => {
    if (!competition) return;

    const root = document.documentElement;
    const previousOverflow = root.style.overflow;

    root.style.overflow = "hidden";

    const onEscape = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onEscape);

    return () => {
      root.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onEscape);
    };
  }, [competition, onClose]);

  if (!competition) return null;

  const temporalStatus = getCompetitionTemporalStatus(competition);
  const disciplines = safeDisciplines(competition.disciplines_competition);
  const percentPresence = getPresencePercent(stats);

  return (
    <div className="fixed inset-0 z-[9999]">
      <button
        type="button"
        aria-label="Fermer"
        onClick={onClose}
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-2xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-950">
        <div className="relative h-56 shrink-0 overflow-hidden bg-slate-900">
          <img
            src={competition.photo_URL || "/img/no-image.jpg"}
            alt={competition.nom_competition || "Compétition"}
            onError={(event) => {
              event.currentTarget.src = "/img/no-image.jpg";
            }}
            className="h-full w-full object-cover opacity-75"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />

          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
            aria-label="Fermer le détail"
          >
            <FaTimes />
          </button>

          <div className="absolute bottom-4 left-4 right-4">
            <div className="mb-3 flex flex-wrap gap-2">
              <Badge className={temporalStatus.tone}>
                {temporalStatus.label}
              </Badge>

              <Badge
                className={
                  competition.is_active
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-slate-100 text-slate-700"
                }
              >
                {competition.is_active ? "Active" : "Inactive"}
              </Badge>

              <Badge
                className={
                  competition.is_public
                    ? "bg-amber-100 text-amber-700"
                    : "bg-slate-100 text-slate-700"
                }
              >
                {competition.is_public ? "Publique" : "Privée"}
              </Badge>
            </div>

            <div className="text-xs font-bold uppercase tracking-[0.18em] text-white/60">
              Compétition #{competition.id}
            </div>

            <h2 className="mt-1 text-2xl font-bold text-white sm:text-3xl">
              {competition.nom_competition || "Compétition sans nom"}
            </h2>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <DetailCard
              icon={FaCalendarAlt}
              label="Date"
              value={formatDateLongFR(competition.date_competition)}
            />

            <DetailCard
              icon={FaMapMarkerAlt}
              label="Lieu"
              value={competition.lieu_competition || "Lieu non renseigné"}
            />

            <DetailCard
              icon={FaUser}
              label="Organisateur"
              value={getOrganizerLabel(
                organizer,
                competition.organisateur_competition
              )}
              helper={organizer?.email}
            />

            <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
              <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
                Niveau
              </div>

              <div className="mt-2">
                <span
                  className={`inline-flex rounded-full px-3 py-1.5 text-xs font-bold ${getLevelTone(
                    competition.level_competition
                  )}`}
                >
                  {getLevelLabel(competition.level_competition)}
                </span>
              </div>
            </div>
          </div>

          <section className="mt-4 rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <h3 className="text-sm font-bold uppercase tracking-wide text-slate-400">
              Disciplines
            </h3>

            <div className="mt-3 flex flex-wrap gap-2">
              {disciplines.length > 0 ? (
                disciplines.map((discipline, index) => (
                  <span
                    key={`${discipline}-${index}`}
                    className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  >
                    {discipline}
                  </span>
                ))
              ) : (
                <span className="text-sm text-slate-500">
                  Aucune discipline renseignée.
                </span>
              )}
            </div>
          </section>

          <section className="mt-4 rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <h3 className="text-sm font-bold uppercase tracking-wide text-slate-400">
              Statistiques de la compétition
            </h3>

            {stats === undefined ? (
              <div className="mt-4 rounded-2xl bg-slate-50 p-4 text-sm font-semibold text-slate-500 dark:bg-slate-950 dark:text-slate-400">
                Chargement des statistiques…
              </div>
            ) : (
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <StatCard
                  icon={FaUsers}
                  label="Inscrits"
                  value={stats.totalInscrits || 0}
                />

                <StatCard
                  icon={FaUserCheck}
                  label="Présents"
                  value={stats.totalPresents || 0}
                />

                <StatCard
                  icon={FaPercentage}
                  label="Présence"
                  value={percentPresence !== null ? `${percentPresence}%` : "—"}
                />

                <StatCard
                  icon={FaClipboardList}
                  label="Distributions"
                  value={stats.distCount || 0}
                />

                <StatCard
                  icon={MdOutlineSportsKabaddi}
                  label="Combats"
                  value={stats.combatCount || 0}
                />
              </div>
            )}
          </section>
        </div>

        <div className="shrink-0 border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
            >
              <FaTimes />
              Fermer
            </button>

            <button
              type="button"
              onClick={() => openCompetitionPublicPage(competition.id)}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-700"
            >
              <FaExternalLinkAlt />
              Ouvrir /competition/{competition.id}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

function DetailCard({ icon: Icon, label, value, helper }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950/70 dark:text-indigo-300">
          <Icon />
        </div>

        <div className="min-w-0">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
            {label}
          </div>

          <div className="mt-1 font-bold text-slate-900 dark:text-white">
            {value || "—"}
          </div>

          {helper ? (
            <div className="mt-1 break-all text-xs font-semibold text-slate-500 dark:text-slate-400">
              {helper}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950/70">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-indigo-600 shadow-sm dark:bg-slate-900 dark:text-indigo-300">
          <Icon />
        </div>

        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
            {label}
          </div>
          <div className="mt-0.5 text-2xl font-bold text-slate-900 dark:text-white">
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

function Badge({ children, className }) {
  return (
    <span
      className={`inline-flex rounded-full px-3 py-1.5 text-xs font-bold ${className}`}
    >
      {children}
    </span>
  );
}